// DevHub CUSTOM — localStorage-backed "recent items" for the command palette.
//
// Tracks the pages/entities the user opened *via the palette* so the empty-term
// state can surface a "Recent" group. Intentionally tiny and fail-safe: any
// storage error (private mode, quota, malformed JSON) degrades to an empty list
// rather than throwing into the modal render path.

import { useCallback, useState } from 'react';
import type { RecentCommand } from './commandRegistry';

const STORAGE_KEY = 'devhub.cmdk.recent';
// Cap the list so it stays a quick-jump affordance, not a history log.
const MAX_RECENT = 6;

function readRecent(): RecentCommand[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Keep only well-formed entries (defensive against older/foreign shapes).
    return parsed.filter(
      (e): e is RecentCommand =>
        e !== null &&
        typeof e === 'object' &&
        typeof (e as RecentCommand).id === 'string' &&
        typeof (e as RecentCommand).title === 'string' &&
        typeof (e as RecentCommand).icon === 'string' &&
        typeof (e as RecentCommand).path === 'string',
    ).slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeRecent(items: RecentCommand[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
  } catch {
    // Ignore — storage may be unavailable (private mode / quota). Non-fatal.
  }
}

/**
 * Recent-items store for the palette. `pushRecent` moves the item to the front
 * (deduped by id), trims to MAX_RECENT, and persists. Reads are lazy-initialised
 * from localStorage on first mount.
 */
export function useRecentItems(): {
  recent: RecentCommand[];
  pushRecent: (item: RecentCommand) => void;
} {
  const [recent, setRecent] = useState<RecentCommand[]>(() => readRecent());

  const pushRecent = useCallback((item: RecentCommand) => {
    setRecent(prev => {
      const next = [item, ...prev.filter(e => e.id !== item.id)].slice(0, MAX_RECENT);
      writeRecent(next);
      return next;
    });
  }, []);

  return { recent, pushRecent };
}
